import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { MutationHookOptions } from '@apollo/client/react/types/types';

import { accountApi } from '../index';
import { accountContactApi } from './index';
import {
  CreateAccountContactInput,
  CreateAccountContactResponse,
  CreateAccountContactVariables,
  UpdateAccountContactInput,
  UpdateAccountContactResponse,
  UpdateAccountContactVariables,
  DeleteAccountContactInput,
  DeleteAccountContactResponse,
  DeleteAccountContactVariables,
} from './types';

export function useAccountContact() {
  const { showNotification } = useNotifications();

  const [onCreate, { loading: isCreating }] = useMutation<CreateAccountContactResponse, CreateAccountContactVariables>(
    accountContactApi.create
  );

  const [onUpdate, { loading: isUpdating }] = useMutation<UpdateAccountContactResponse, UpdateAccountContactVariables>(
    accountContactApi.update
  );

  const [onDelete, { loading: isDeleting }] = useMutation<DeleteAccountContactResponse, DeleteAccountContactVariables>(
    accountContactApi.delete
  );

  const handleCreateAccountContact = useCallback(
    async (
      input: CreateAccountContactInput,
      fetchOptions?: (api: typeof accountApi) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        const { data } = await onCreate({
          variables: {
            input,
          },
          ...(typeof fetchOptions == 'function' ? fetchOptions(accountApi) : {}),
        });

        const result = data?.accountMutation?.result;

        if (result?.success) {
          showNotification('Account Contact created successfully');
        } else {
          showNotification(result?.error ?? 'Failed to create Account Contact', { type: 'error' });
        }

        return result;
      } catch (error: unknown) {
        showNotification('Failed to create Account Contact', { type: 'error' });
        logError(error);
      }
    },
    [onCreate, showNotification]
  );

  const handleUpdateAccountContact = useCallback(
    async (
      input: UpdateAccountContactInput,
      fetchOptions?: (api: typeof accountApi) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        const { data } = await onUpdate({
          variables: {
            input,
          },
          ...(typeof fetchOptions == 'function' ? fetchOptions(accountApi) : {}),
        });

        const result = data?.accountMutation?.result;

        if (result?.success) {
          showNotification('Account Contact updated successfully');
        } else {
          showNotification(result?.error ?? 'Failed to update Account Contact', { type: 'error' });
        }

        return result;
      } catch (error: unknown) {
        showNotification('Failed to update Account Contact', { type: 'error' });
        logError(error);
      }
    },
    [onUpdate, showNotification]
  );

  const handleDeleteAccountContact = useCallback(
    async (
      input: DeleteAccountContactInput,
      fetchOptions?: (api: typeof accountApi) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        const { data } = await onDelete({
          variables: {
            input,
          },
          ...(typeof fetchOptions == 'function' ? fetchOptions(accountApi) : {}),
        });

        const result = data?.accountMutation?.result;

        if (result?.success) {
          showNotification('Account Contact deleted successfully');
        } else {
          showNotification(result?.error ?? 'Failed to delete Account Contact', { type: 'error' });
        }

        return result;
      } catch (error: unknown) {
        showNotification('Failed to delete Account Contact', { type: 'error' });
        logError(error);
      }
    },
    [onDelete, showNotification]
  );

  return {
    isCreating,
    isUpdating,
    isDeleting,
    handleCreateAccountContact,
    handleUpdateAccountContact,
    handleDeleteAccountContact,
  };
}
